import React, { useMemo, useState, useEffect, useRef } from 'react';

import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useGetTagKeys } from '@src/services/query/applang/queryHooks';
import { useCriticalEventInterval } from '@src/features/CriticalEventFormContainer';
import { AutoComplete } from './styled';

interface TagKeyAutoCompleteProps {
  rawEventName?: string;
  rule: any;
  onChange?: (value: any) => void;
  placeholder?: string;
  filterOption?: boolean;
  status?: '' | 'warning' | 'error';
  disabled?: boolean;
  [key: string]: any;
}

const TagKeyAutoComplete: React.FC<TagKeyAutoCompleteProps> = ({ rawEventName, rule, ...rest }) => {
  // State for search input (immediate update for UI)
  const [searchInput, setSearchInput] = useState<string>('');
  // Debounced search key for API calls
  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchKey(searchInput);
    }, 300); // 300ms debounce

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Try to get interval from either context (CriticalEvent or Dimension)
  const interval = useCriticalEventInterval();

  // Fetch tag keys from API with debounced search key
  const { data: tagKeys, isFetching: isLoading } = useGetTagKeys(
    {
      event_name: rawEventName || undefined,
      time_range: interval,
      key: debouncedSearchKey, // Use debounced search key for filtering
    },
    { enabled: !!isDropdownOpen },
  );

  // Transform tag keys to options format
  const tagKeyOptions = useMemo(() => {
    if (!tagKeys || tagKeys.length === 0) return [];
    return tagKeys.map((key) => ({
      label: key,
      value: key,
    }));
  }, [tagKeys]);

  const Suffix = useMemo(() => {
    if (isLoading) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return <DownOutlined />;
  }, [isLoading]);

  return (
    <AutoComplete
      showSearch
      value={rule.tag_key.value[0] || undefined}
      options={tagKeyOptions}
      suffixIcon={Suffix}
      style={{ width: '100%' }}
      allowClear={true}
      onSearch={(value) => {
        // Update search input (will be debounced for API calls)
        setSearchInput(value || '');
      }}
      onClear={() => {
        setSearchInput('');
        setDebouncedSearchKey('');
      }}
      {...rest}
      onOpenChange={(open) => {
        setIsDropdownOpen(open);
      }}
      popupMatchSelectWidth={false}
    />
  );
};

export default TagKeyAutoComplete;
